/**
 * SAY WHICH COLUMN IS WHICH — for a sales or purchase list kept in the client's own layout (owner, 2026-10-16: a live
 * client keeps daily sales in a sheet nobody designed for Leveri). The list is shown as the server found it in the
 * work's records: its headings, its first lines, and Leveri's proposal — including, when no column names a customer,
 * that every line is a cash sale. The person confirms with one click, or changes a column first; Leveri files the
 * mapping in their name and the request is answered through the same gate as any answer. The screen reads nothing
 * itself: the server refuses a mapping that reads no lines, in the reader's own words.
 */
import { useState } from "react";
import { WorkActionResponse, type OpenRequest, type WorkFile } from "@leveri/contracts";
import { Button, Notice, Reading, Select } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { useKeyring } from "./keys.js";

type Sheet = NonNullable<OpenRequest["mappableLedgers"]>[number];
type Role = keyof Sheet["proposed"];
const ROLES: readonly Role[] = ["date", "party", "amount", "tax", "net", "number"];
const NUM = "[font-variant-numeric:tabular-nums]";

export function LedgerColumns({ workId, request, onWork, onOutcome }: {
  workId: string; request: OpenRequest; onWork: (work: WorkFile) => void; onOutcome: (outcome: string) => void;
}) {
  const sheets = request.mappableLedgers ?? [];
  const [maps, setMaps] = useState<Array<Record<Role, number | null>>>(() => sheets.map((s) => ({ ...s.proposed })));
  const [cash, setCash] = useState<boolean[]>(() => sheets.map((s) => s.cashProposed));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();
  if (sheets.length === 0) return null;
  const ready = maps.every((m, i) => m.date !== null && (m.party !== null || cash[i]) && (m.amount !== null || (m.net !== null && m.tax !== null)));

  function set(sheetIndex: number, role: Role, value: string) {
    const at = value === "" ? null : Number(value);
    setMaps((now) => now.map((m, i) => {
      if (i !== sheetIndex) return m;
      // A column means one thing: choosing it for this role frees it from any other.
      const next = { ...m };
      if (at !== null) for (const r of Object.keys(next) as Role[]) if (next[r] === at) next[r] = null;
      next[role] = at;
      return next;
    }));
    // Naming a customer column means the lines are not all cash sales.
    if (role === "party" && at !== null) setCash((now) => now.map((c, i) => (i === sheetIndex ? false : c)));
  }

  async function send() {
    if (busy || !ready) return;
    setBusy(true);
    setFailure(null);
    try {
      const mappings = sheets.map((s, i) => {
        const m = maps[i]!;
        return { evidenceReferenceId: s.evidenceReferenceId, sheetName: s.sheetName, columns: { ...m, ...(m.party === null && cash[i] ? { cashSales: true } : {}) } };
      });
      const result = await call("POST", `/api/work/${workId}/requests/${request.requirementId}/ledger-columns`, WorkActionResponse, {
        mappings,
        // One act per mapping: pressing the button twice is one act; a different mapping is another.
        submissionKey: keyFor(`ledger:${request.requirementId}:${JSON.stringify(mappings)}`),
      });
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-rule-soft pt-4" data-testid="ledger-columns">
      <p className="text-[13px] text-stone">
        Leveri could not tell which column is which from the headings. It has proposed a reading below — confirm it, or change a column first.
        The mapping is filed in your name; the sheet itself is not changed.
      </p>
      {sheets.map((sheet, s) => (
        <div key={`${sheet.evidenceReferenceId}:${sheet.sheetName}`} className="mt-4">
          {sheets.length > 1 && <p className="text-[14px] font-medium text-ink">Sheet “{sheet.sheetName}”</p>}
          <p className="text-[12px] text-stone">{sheet.reason}</p>
          {/* The first lines, as the record prints them: wide, so it scrolls in its own box on a phone. */}
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[420px] text-[12px]">
              <thead>
                <tr className="border-b border-rule text-left text-stone">
                  {sheet.headings.map((h, i) => <th key={`${i}:${h}`} className="py-1.5 pr-3 font-normal">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {sheet.sample.map((row, r) => (
                  <tr key={r} className="border-b border-rule-soft text-ink">
                    {row.map((cell, i) => <td key={i} className={`whitespace-nowrap py-1.5 pr-3 ${NUM}`}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {ROLES.map((role) => (
              <Select key={role} label={sheet.roleWords[role]} value={maps[s]?.[role] ?? ""} disabled={busy || (role === "party" && cash[s] === true)} onChange={(e) => set(s, role, e.target.value)}
                data-testid={`ledger-map-${role}`}>
                <option value="">Not in this sheet</option>
                {sheet.headings.map((h, i) => <option key={`${i}:${h}`} value={i}>{h}</option>)}
              </Select>
            ))}
          </div>
          <label className="mt-3 flex items-start gap-2 text-[13px] text-ink">
            <input type="checkbox" className="mt-0.5" checked={cash[s] === true} disabled={busy}
              onChange={(e) => { const on = e.target.checked; setCash((now) => now.map((c, i) => (i === s ? on : c))); if (on) set(s, "party", ""); }}
              data-testid="ledger-cash" />
            <span>
              {sheet.side === "SALES" ? "Every line is a cash sale — no customer is named" : "Every line is a cash purchase — no supplier is named"}
            </span>
          </label>
        </div>
      ))}
      <div className="mt-4 flex flex-wrap items-center gap-3.5">
        <Button variant="secondary" disabled={busy || !ready} onClick={() => void send()} data-testid="ledger-columns-send">Read it this way</Button>
        {!ready && <span className="text-[13px] text-stone">Choose the date and the amount, and who the lines are with (or tick cash).</span>}
      </div>
      {busy && <div className="mt-3"><Reading>Filing the mapping, and carrying on with the work…</Reading></div>}
      {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
    </div>
  );
}
